import { UserType } from "@elation/api-client/src/stitch-web/models/userType";
import { FormikInput } from "@src/components/forms/FormikInput";
import { FormikSelect } from "@src/components/forms/FormikSelect";
import { HeaderText } from "@src/components/general/HeaderText";
import { Text } from "@src/components/general/Text";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/components/layout/Flexbox";
import { Button } from "@src/components/ui/button";
import { Separator } from "@src/components/ui/separator";
import { IS_CHROME_EXTENSION } from "@src/env";
import { navigation } from "@src/navigation";
import { LoginLayout } from "@src/pages/LoginLayout";
import { Formik } from "formik";
import { useNavigate } from "react-router";
import * as yup from "yup";

const validationSchema = yup.object<Values>({
  email: yup.string().email().required(),
  password: yup.string().required(),
  type: yup.mixed().oneOf(Object.values(UserType)),
});

export type Values = {
  email: string;
  password: string;
  type: UserType;
};

export interface LoginPageProps {
  onLogin?: (values: Values) => Promise<void>;
  onLoginWithElation?: (values: Values) => Promise<void>;
  isLoading?: boolean;
  error?: string | null;
}

export const LoginPage = ({
  onLogin = async () => {
    return;
  },
  onLoginWithElation = async () => {
    return;
  },
  isLoading = false,
  error = null,
}: LoginPageProps) => {
  const navigate = useNavigate();
  return (
    <LoginLayout>
      <Formik<Values>
        validationSchema={validationSchema}
        initialValues={{ email: "", password: "", type: "physician" }}
        onSubmit={async () => {
          return Promise.resolve();
        }}
      >
        {({ values, handleSubmit }) => {
          return (
            <VerticalFlexbox
              as="form"
              gap="lg"
              className="w-full max-w-md"
              onSubmit={handleSubmit}
            >
              <VerticalFlexbox gap="xs">
                <HeaderText tag="h1" className="text-4xl leading-tight">
                  Where every
                  <br />
                  <Text as="span" className="text-accent">
                    thread meets.
                  </Text>
                </HeaderText>
                <Text sizing="sm" appearance="subdued">
                  Sign in to your Stitch account to continue.
                </Text>
              </VerticalFlexbox>

              {error ? (
                <Text
                  appearance="danger"
                  role="alert"
                  sizing="sm"
                  className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2"
                >
                  {error}
                </Text>
              ) : null}

              <VerticalFlexbox gap="md">
                <FormikInput
                  name="email"
                  label="Email"
                  type="email"
                  autoComplete="email"
                  required
                />
                <FormikInput
                  name="password"
                  label="Password"
                  type="password"
                  autoComplete="current-password"
                  required
                />
                {IS_CHROME_EXTENSION ? (
                  <FormikSelect
                    name="type"
                    label="I'm logging in as a"
                    items={[
                      { label: "Physician", value: "physician" },
                      { label: "Staff", value: "staff" },
                    ]}
                    required
                  />
                ) : null}
              </VerticalFlexbox>

              <VerticalFlexbox gap="sm">
                <Button
                  type="button"
                  className="h-11 bg-accent text-accent-foreground hover:bg-accent/90"
                  name="login"
                  disabled={isLoading}
                  onClick={() => onLogin(values)}
                >
                  {isLoading ? "Signing in..." : "Login"}
                </Button>
                <HorizontalFlexbox
                  alignItems="center"
                  gap="sm"
                  className="text-xs uppercase tracking-wider text-foreground/60"
                >
                  <Separator className="flex-1" />
                  <Text as="span" sizing="xs" fontWeight={500}>
                    OR
                  </Text>
                  <Separator className="flex-1" />
                </HorizontalFlexbox>
                <Button
                  type="button"
                  variant="default"
                  className="h-11"
                  name="login-with-elation"
                  disabled={isLoading}
                  onClick={() => onLoginWithElation(values)}
                >
                  {isLoading ? "Signing in..." : "Login with Elation"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="h-11"
                  name="create-account"
                  disabled={isLoading}
                  onClick={() => navigate(navigation.createAccount())}
                >
                  Create an Account
                </Button>
              </VerticalFlexbox>
            </VerticalFlexbox>
          );
        }}
      </Formik>
    </LoginLayout>
  );
};
